/*
 * ==========================================================================
 * KALENDER-ANSICHT (Fullscreen-Overlay)
 * ==========================================================================
 * Zeigt Cockpit-Termine + Google-eigene Events als grau-blaue Blocker in
 * einer Monats-Grid-Ansicht. Darüber eine Halbjahres-Miniatur, die auf
 * einen Blick sehen lässt, wie voll der Rhythmus ist.
 *
 * Props:
 *   isOffen           Boolean       Render nur wenn true.
 *   kunde             Obj | null    { id, firstName, lastName } — wenn gesetzt,
 *                                   ist der Kalender im "Folge-Termin"-Modus:
 *                                   Klick auf einen leeren Tag legt direkt fuer
 *                                   diesen Kunden an (sonst Kundenauswahl im Modal).
 *   onClose()                       Zurueck ins Dashboard.
 *   onAnlageOeffnen(datumISO, uhrzeit, kundeId)
 *                                   Parent öffnet Termin-Anlage-Modal.
 *   onEditOeffnen(termin)           Parent öffnet Edit-Termin-Modal.
 *   refreshKey        any           Aendert sich, wenn ein Termin
 *                                   angelegt/aktualisiert wurde — triggert Re-Fetch.
 *
 * Farbsystem (siehe CI):
 *   Brombeer = #85366C   Haupt-Akzent (aktiver Tag, aktiver Monat)
 *   Orchidee = #DC91D0   Hover / Sekundaer-Akzent
 *   Blau     = #2DBAE8   Video-Termin-Chip
 *   Gruen    = #8CC53F   Hausbesuch-Chip
 *   Grau     = slate-*   Google-eigener Blocker
 * ==========================================================================
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  ChevronLeft, ChevronRight, ArrowLeft, Calendar as CalendarIcon,
  Loader2, Plus, Video, MapPin, Lock, X,
} from 'lucide-react';
import { api, ApiError } from '../api';

const CI = {
  brombeer: '#85366C',
  orchidee: '#DC91D0',
  blue: '#2DBAE8',
  green: '#8CC53F',
};

const MONATE = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember',
];
const MONATE_KURZ = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];
const WOCHENTAGE = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
const WOCHENTAGE_MINI = ['M', 'D', 'M', 'D', 'F', 'S', 'S'];

// ISO-Datum 'YYYY-MM-DD' aus (jahr, monat 0-11, tag)
const isoDatum = (j, m, t) => `${j}-${String(m + 1).padStart(2, '0')}-${String(t).padStart(2, '0')}`;

// Wochentag-Index mit Montag=0 (JS-Default ist Sonntag=0)
const wochentagMoBase = (d) => {
  const w = d.getDay();
  return w === 0 ? 6 : w - 1;
};

const tageImMonat = (jahr, monat /* 0-11 */) => new Date(jahr, monat + 1, 0).getDate();

// ISO-Kalenderwoche fuer ein Datum (Woche 1 = erste Woche mit >= 4 Tagen im Jahr).
const isoKW = (date) => {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
};

// Montag der ISO-KW als Date-Objekt (lokale Zeitzone).
const montagDerKW = (jahr, kw) => {
  const simple = new Date(jahr, 0, 1 + (kw - 1) * 7);
  const dow = simple.getDay();
  const mo = new Date(simple);
  if (dow <= 4) mo.setDate(simple.getDate() - simple.getDay() + 1);
  else mo.setDate(simple.getDate() + 8 - simple.getDay());
  return mo;
};

// 'YYYY-MM-DDTHH:MM:SS+02:00' -> { datum: 'YYYY-MM-DD', uhrzeit: 'HH:MM' }
const parseIsoLokal = (s) => {
  if (!s) return { datum: '', uhrzeit: '' };
  if (s.length === 10) return { datum: s, uhrzeit: '' };  // ganztaegig
  return { datum: s.slice(0, 10), uhrzeit: s.slice(11, 16) };
};

export default function KalenderAnsicht({
  isOffen, kunde, onClose,
  onAnlageOeffnen, onEditOeffnen,
  refreshKey,
}) {
  const heute = new Date();
  const [anzeigeMonat, setAnzeigeMonat] = useState(() => ({
    jahr: heute.getFullYear(),
    monat: heute.getMonth(),
  }));
  const halbjahr = useMemo(() => ({
    jahr: anzeigeMonat.jahr,
    h: anzeigeMonat.monat < 6 ? 1 : 2,
  }), [anzeigeMonat]);

  const [termine, setTermine] = useState([]);
  const [googleEvents, setGoogleEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fehler, setFehler] = useState(null);
  // Wochen-Ansicht-Overlay: { jahr, kw } oder null (geschlossen).
  const [wochenOffen, setWochenOffen] = useState(null);

  // Laden der Daten fuer das aktuelle Halbjahr. Halbjahr wechselt automatisch
  // mit dem Monat — deshalb dependency auf Halbjahr, nicht auf Monat.
  useEffect(() => {
    if (!isOffen) return;
    let abgebrochen = false;
    (async () => {
      setLoading(true);
      setFehler(null);
      const von = `${halbjahr.jahr}-${halbjahr.h === 1 ? '01' : '07'}-01`;
      const bis = `${halbjahr.jahr}-${halbjahr.h === 1 ? '06-30' : '12-31'}`;
      try {
        const [t, g] = await Promise.all([
          api.get(`/api/termine?von=${von}&bis=${bis}`),
          api.get(`/api/kalender/google-events?von=${von}&bis=${bis}`).catch(() => []),
        ]);
        if (!abgebrochen) {
          setTermine(t || []);
          setGoogleEvents(g || []);
        }
      } catch (err) {
        if (!abgebrochen) {
          setFehler(err instanceof ApiError ? (err.detail || 'Fehler beim Laden.') : 'Verbindung zum Server fehlgeschlagen.');
        }
      } finally {
        if (!abgebrochen) setLoading(false);
      }
    })();
    return () => { abgebrochen = true; };
  }, [isOffen, halbjahr.jahr, halbjahr.h, refreshKey]);

  // Termine und Google-Events nach Datum gruppieren — fuer schnellen Zugriff.
  const terminePerTag = useMemo(() => {
    const map = {};
    for (const t of termine) {
      if (!t.date) continue;
      (map[t.date] = map[t.date] || []).push(t);
    }
    // Pro Tag chronologisch sortieren
    Object.values(map).forEach(arr => arr.sort((a, b) => (a.time || '').localeCompare(b.time || '')));
    return map;
  }, [termine]);

  const googlePerTag = useMemo(() => {
    const map = {};
    for (const g of googleEvents) {
      const { datum } = parseIsoLokal(g.start);
      if (!datum) continue;
      (map[datum] = map[datum] || []).push(g);
    }
    return map;
  }, [googleEvents]);

  // ---- Navigation ----
  const vorMonat = () => {
    setAnzeigeMonat(prev => {
      let m = prev.monat - 1, j = prev.jahr;
      if (m < 0) { m = 11; j -= 1; }
      return { jahr: j, monat: m };
    });
  };
  const nachMonat = () => {
    setAnzeigeMonat(prev => {
      let m = prev.monat + 1, j = prev.jahr;
      if (m > 11) { m = 0; j += 1; }
      return { jahr: j, monat: m };
    });
  };
  const heuteSpringen = () => setAnzeigeMonat({ jahr: heute.getFullYear(), monat: heute.getMonth() });
  const halbjahrWechseln = (j, h) => setAnzeigeMonat({ jahr: j, monat: h === 1 ? 0 : 6 });

  if (!isOffen) return null;

  return (
    <div className="fixed inset-0 bg-slate-50 z-[60] overflow-y-auto">
      {/* ===== Kopfzeile ===== */}
      <header className="sticky top-0 z-20 bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-[1600px] mx-auto px-6 py-3 flex items-center justify-between gap-4">
          <button onClick={onClose} className="flex items-center gap-2 px-3 py-2 rounded-lg text-gray-700 hover:bg-gray-100 font-bold text-sm transition-colors">
            <ArrowLeft className="w-4 h-4" />
            {kunde
              ? <>Zurück zur Akte von <span style={{ color: CI.brombeer }}>{kunde.firstName} {kunde.lastName}</span></>
              : 'Zurück ins Cockpit'}
          </button>
          <div className="flex items-center gap-3">
            <CalendarIcon className="w-5 h-5" style={{ color: CI.brombeer }} />
            <h1 className="text-lg font-bold text-gray-800">
              Kalender
              {kunde && (
                <span className="ml-2 font-normal text-gray-500">
                  — Folge-Termin für <b className="text-gray-800">{kunde.firstName} {kunde.lastName}</b>
                </span>
              )}
            </h1>
          </div>
          {(() => {
            const istAufHeute = anzeigeMonat.jahr === heute.getFullYear() && anzeigeMonat.monat === heute.getMonth();
            return (
              <button
                onClick={heuteSpringen}
                disabled={istAufHeute}
                className={`px-4 py-2 rounded-lg text-sm font-bold shadow-sm transition-all ${istAufHeute ? 'cursor-default opacity-70 ring-2 ring-offset-2 ring-fuchsia-300 text-white' : 'text-white hover:-translate-y-0.5'}`}
                style={{ backgroundColor: CI.brombeer }}
                title={istAufHeute ? 'Du bist schon im aktuellen Monat.' : 'Zum aktuellen Monat springen'}
              >
                Heute
              </button>
            );
          })()}
        </div>
      </header>

      <div className="max-w-[1600px] mx-auto px-6 py-6 space-y-6">

        {/* ===== Halbjahres-Miniatur ===== */}
        <section className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <button onClick={() => halbjahrWechseln(halbjahr.h === 1 ? halbjahr.jahr - 1 : halbjahr.jahr, halbjahr.h === 1 ? 2 : 1)} className="flex items-center gap-1 px-2 py-1 rounded hover:bg-gray-100 text-gray-600 text-sm"><ChevronLeft className="w-4 h-4" /> vorheriges Halbjahr</button>
            <h2 className="text-sm font-bold uppercase tracking-wider text-gray-700">
              {halbjahr.h}. Halbjahr {halbjahr.jahr}
            </h2>
            <button onClick={() => halbjahrWechseln(halbjahr.h === 2 ? halbjahr.jahr + 1 : halbjahr.jahr, halbjahr.h === 2 ? 1 : 2)} className="flex items-center gap-1 px-2 py-1 rounded hover:bg-gray-100 text-gray-600 text-sm">nächstes Halbjahr <ChevronRight className="w-4 h-4" /></button>
          </div>
          <div className="grid grid-cols-6 gap-4">
            {Array.from({ length: 6 }, (_, i) => {
              const m = halbjahr.h === 1 ? i : i + 6;
              return (
                <MiniMonat
                  key={m}
                  jahr={halbjahr.jahr}
                  monat={m}
                  istAktiv={anzeigeMonat.monat === m && anzeigeMonat.jahr === halbjahr.jahr}
                  terminePerTag={terminePerTag}
                  googlePerTag={googlePerTag}
                  onMonatKlick={() => setAnzeigeMonat({ jahr: halbjahr.jahr, monat: m })}
                  onTagKlick={(tag) => {
                    // Klick auf einen Tag in der Mini-Ansicht: zum Monat
                    // springen UND direkt das Anlage-Modal oeffnen, damit
                    // der Folgetermin-Workflow ohne Umweg ueber die
                    // Wochenansicht funktioniert.
                    setAnzeigeMonat({ jahr: halbjahr.jahr, monat: m });
                    const datum = `${halbjahr.jahr}-${String(m + 1).padStart(2, '0')}-${String(tag).padStart(2, '0')}`;
                    onAnlageOeffnen(datum, '10:00', kunde?.id || null);
                  }}
                />
              );
            })}
          </div>
        </section>

        {/* ===== Haupt-Monatsansicht ===== */}
        <section className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <button onClick={vorMonat} className="flex items-center gap-1 px-3 py-1.5 rounded hover:bg-gray-100 text-gray-700 font-bold text-sm">
              <ChevronLeft className="w-4 h-4" /> {MONATE[anzeigeMonat.monat === 0 ? 11 : anzeigeMonat.monat - 1]}
            </button>
            <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-3">
              {MONATE[anzeigeMonat.monat]} {anzeigeMonat.jahr}
              {loading && <Loader2 className="w-4 h-4 animate-spin text-gray-400" title="Lade Termine…" />}
            </h2>
            <button onClick={nachMonat} className="flex items-center gap-1 px-3 py-1.5 rounded hover:bg-gray-100 text-gray-700 font-bold text-sm">
              {MONATE[anzeigeMonat.monat === 11 ? 0 : anzeigeMonat.monat + 1]} <ChevronRight className="w-4 h-4" />
            </button>
          </div>

          {fehler && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm mb-4">{fehler}</div>
          )}

          <MonatsGrid
            jahr={anzeigeMonat.jahr}
            monat={anzeigeMonat.monat}
            terminePerTag={terminePerTag}
            googlePerTag={googlePerTag}
            heute={heute}
            kunde={kunde}
            onLeerKlick={(datum, uhrzeit = '10:00') => onAnlageOeffnen(datum, uhrzeit, kunde?.id || null)}
            onTerminKlick={(termin) => onEditOeffnen(termin)}
            onKWKlick={(jahr, kw) => setWochenOffen({ jahr, kw })}
          />
        </section>

        {/* ===== Legende ===== */}
        <section className="bg-white rounded-xl shadow-sm border border-gray-200 px-5 py-3">
          <div className="flex flex-wrap gap-4 items-center text-xs text-gray-600">
            <div className="flex items-center gap-1.5"><span className="w-3 h-3 rounded" style={{ backgroundColor: CI.green }}></span>Hausbesuch</div>
            <div className="flex items-center gap-1.5"><span className="w-3 h-3 rounded" style={{ backgroundColor: CI.blue }}></span>Videotermin</div>
            <div className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-slate-300 border border-slate-400"></span>Privat (Google-Kalender)</div>
            <div className="flex items-center gap-1.5"><span className="w-3 h-3 rounded border-2" style={{ borderColor: CI.brombeer }}></span>Heute</div>
            <div className="ml-auto italic text-gray-500">Klick auf die KW-Nummer links öffnet die Wochenansicht mit Stunden-Raster.</div>
          </div>
        </section>
      </div>

      {/* ===== Wochenansicht-Overlay (ueber dem Kalender-Overlay) ===== */}
      {wochenOffen && (
        <WochenAnsicht
          jahr={wochenOffen.jahr}
          kw={wochenOffen.kw}
          terminePerTag={terminePerTag}
          googlePerTag={googlePerTag}
          kunde={kunde}
          onClose={() => setWochenOffen(null)}
          onBackToAkte={() => { setWochenOffen(null); onClose(); }}
          onLeerKlick={(datum, uhrzeit) => onAnlageOeffnen(datum, uhrzeit, kunde?.id || null)}
          onTerminKlick={(termin) => onEditOeffnen(termin)}
          onWeekChange={(jahrNeu, kwNeu) => {
            setWochenOffen({ jahr: jahrNeu, kw: kwNeu });
            // Wenn die neue Woche in einem anderen Monat liegt, ziehen wir
            // den Hauptmonat im Hintergrund mit, damit beim Schliessen die
            // Monatsansicht beim richtigen Stand ist.
            const m = montagDerKW(jahrNeu, kwNeu);
            setAnzeigeMonat({ jahr: m.getFullYear(), monat: m.getMonth() });
          }}
        />
      )}
    </div>
  );
}

// ==========================================================================
// Haupt-Monatsgrid (KW-Spalte + 7 Wochentagsspalten, 5-6 Zeilen)
// ==========================================================================
function MonatsGrid({ jahr, monat, terminePerTag, googlePerTag, heute, kunde, onLeerKlick, onTerminKlick, onKWKlick }) {
  const anzahlTage = tageImMonat(jahr, monat);
  const erster = new Date(jahr, monat, 1);
  const startOffset = wochentagMoBase(erster);  // 0=Mo ... 6=So
  const gesamtZellen = Math.ceil((startOffset + anzahlTage) / 7) * 7;
  const anzahlZeilen = gesamtZellen / 7;
  const heuteDatum = isoDatum(heute.getFullYear(), heute.getMonth(), heute.getDate());

  // Daten in Zeilen gruppieren, damit wir pro Zeile die KW vorne mitgeben koennen.
  const zeilen = [];
  for (let z = 0; z < anzahlZeilen; z++) {
    const zeilenZellen = [];
    let montagDatum = null;
    for (let w = 0; w < 7; w++) {
      const i = z * 7 + w;
      const tagNum = i - startOffset + 1;
      if (tagNum < 1 || tagNum > anzahlTage) {
        zeilenZellen.push({ leer: true });
      } else {
        const datum = isoDatum(jahr, monat, tagNum);
        const wTag = (startOffset + tagNum - 1) % 7;
        const zellObj = {
          tag: tagNum,
          datum,
          istWochenende: wTag >= 5,
          istHeute: datum === heuteDatum,
          istVergangen: datum < heuteDatum,
          termine: terminePerTag[datum] || [],
          googleEvents: googlePerTag[datum] || [],
        };
        zeilenZellen.push(zellObj);
        if (w === 0) montagDatum = new Date(jahr, monat, tagNum);
      }
    }
    // KW-Anker: erster Tag der Zeile, der im Monat liegt (faellt die Zeile
    // auf den Monatsanfang, ist Montag evtl. leer — dann Dienstag/etc.).
    const kwAnker = zeilenZellen.find(c => !c.leer);
    let kw = null;
    let kwJahr = jahr;
    if (kwAnker) {
      const [y, m, d] = kwAnker.datum.split('-').map(Number);
      const ankerDate = new Date(y, m - 1, d);
      kw = isoKW(ankerDate);
      // Jahreswechsel: KW 52/53 im Januar gehoert zum Vorjahr, KW 1 im
      // Dezember zum Folgejahr. isoKW gibt die Nummer; fuers Anklicken
      // brauchen wir das Jahr der KW.
      if (m === 12 && kw === 1) kwJahr = y + 1;
      else if (m === 1 && kw >= 52) kwJahr = y - 1;
      else kwJahr = y;
    }
    zeilen.push({ zellen: zeilenZellen, kw, kwJahr });
  }

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      {/* Wochentags-Kopfzeile */}
      <div className="grid bg-gray-50 border-b border-gray-200" style={{ gridTemplateColumns: '3.5rem repeat(7, 1fr)' }}>
        <div className="px-2 py-2 text-[10px] font-bold text-gray-400 uppercase tracking-wider text-center border-r border-gray-200">KW</div>
        {WOCHENTAGE.map(w => (
          <div key={w} className="px-3 py-2 text-xs font-bold text-gray-500 uppercase tracking-wider text-center">{w}</div>
        ))}
      </div>
      {/* Zeilen */}
      {zeilen.map((z, zi) => (
        <div key={zi} className="grid" style={{ gridTemplateColumns: '3.5rem repeat(7, 1fr)' }}>
          <button
            onClick={() => z.kw && onKWKlick(z.kwJahr, z.kw)}
            disabled={!z.kw}
            className="border-r border-gray-200 bg-slate-50 hover:bg-fuchsia-50 flex items-center justify-center text-xs font-bold text-gray-500 hover:text-fuchsia-800 transition-colors cursor-pointer disabled:cursor-default disabled:hover:bg-slate-50"
            title={z.kw ? `Kalenderwoche ${z.kw} — Klick öffnet Wochen-Detail` : ''}
          >
            {z.kw ? `KW ${z.kw}` : ''}
          </button>
          {z.zellen.map((zelle, idx) => (
            <TagesZelle
              key={idx}
              zelle={zelle}
              onLeerKlick={onLeerKlick}
              onTerminKlick={onTerminKlick}
              onMehrKlick={() => z.kw && onKWKlick(z.kwJahr, z.kw)}
              zeigePlusIcon={!!kunde}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

function TagesZelle({ zelle, onLeerKlick, onTerminKlick, onMehrKlick, zeigePlusIcon }) {
  if (zelle.leer) {
    return <div className="min-h-[110px] bg-slate-50 border border-slate-100"></div>;
  }
  const hatTermine = zelle.termine.length > 0;
  const zellenBg = zelle.istVergangen ? 'bg-gray-50/60' : (zelle.istWochenende ? 'bg-slate-50' : 'bg-white');
  const heuteBorder = zelle.istHeute ? { borderColor: CI.brombeer, borderWidth: 2 } : {};

  const clickLeer = (e) => {
    // Nur anlegen, wenn NICHT auf einen Termin-Chip geklickt wurde
    if (e.target.closest('[data-termin-chip]')) return;
    if (e.target.closest('[data-google-chip]')) return;
    onLeerKlick(zelle.datum);
  };

  return (
    <div
      className={`relative min-h-[110px] p-1.5 border border-gray-100 cursor-pointer hover:bg-fuchsia-50/40 transition-colors group ${zellenBg}`}
      style={heuteBorder}
      onClick={clickLeer}
    >
      <div className="flex items-center justify-between mb-1">
        <span className={`text-xs font-bold ${zelle.istHeute ? 'text-white rounded-full px-1.5' : (zelle.istVergangen ? 'text-gray-400' : 'text-gray-700')}`} style={zelle.istHeute ? { backgroundColor: CI.brombeer } : {}}>
          {zelle.tag}
        </span>
        {zeigePlusIcon && !zelle.istVergangen && (
          <span className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5 rounded-full bg-white border border-gray-200" title="Hier Folge-Termin anlegen">
            <Plus className="w-3 h-3" style={{ color: CI.brombeer }} />
          </span>
        )}
      </div>
      <div className="space-y-0.5">
        {zelle.termine.slice(0, 3).map(t => (
          <button
            key={t.id}
            data-termin-chip
            onClick={(e) => { e.stopPropagation(); onTerminKlick(t); }}
            className="block w-full text-left px-1.5 py-0.5 rounded text-[11px] font-bold text-white truncate hover:opacity-80 transition-opacity"
            style={{ backgroundColor: t.type === 'video' ? CI.blue : CI.green }}
            title={`${t.time} ${t.lastName}, ${t.firstName}${t.type === 'video' ? ' (Video)' : ''}`}
          >
            <span className="inline-flex items-center gap-1">
              {t.type === 'video' ? <Video className="w-2.5 h-2.5" /> : <MapPin className="w-2.5 h-2.5" />}
              {t.time} {t.lastName || '—'}
            </span>
          </button>
        ))}
        {zelle.termine.length > 3 && (
          <button
            data-termin-chip
            onClick={(e) => { e.stopPropagation(); onMehrKlick && onMehrKlick(); }}
            className="block w-full text-left text-[10px] text-fuchsia-700 italic px-1.5 hover:underline"
            title="Alle Termine dieser Woche im Stunden-Raster ansehen"
          >
            + {zelle.termine.length - 3} weitere…
          </button>
        )}
        {zelle.googleEvents.slice(0, 2).map(g => {
          const { uhrzeit } = parseIsoLokal(g.start);
          return (
            <div
              key={g.google_event_id}
              data-google-chip
              className="block px-1.5 py-0.5 rounded text-[11px] text-slate-700 bg-slate-200 border border-slate-300 truncate cursor-not-allowed"
              title={`Privat: ${g.summary}`}
            >
              <span className="inline-flex items-center gap-1">
                <Lock className="w-2.5 h-2.5" />
                {g.all_day ? 'ganztägig' : uhrzeit} Privat
              </span>
            </div>
          );
        })}
        {zelle.googleEvents.length > 2 && (
          <button
            data-google-chip
            onClick={(e) => { e.stopPropagation(); onMehrKlick && onMehrKlick(); }}
            className="block w-full text-left text-[10px] text-slate-600 italic px-1.5 hover:underline"
            title="Alle privaten Termine dieser Woche im Stunden-Raster ansehen"
          >
            + {zelle.googleEvents.length - 2} private…
          </button>
        )}
      </div>
    </div>
  );
}

// ==========================================================================
// Halbjahres-Mini-Monat (sechs davon nebeneinander oben)
// ==========================================================================
function MiniMonat({ jahr, monat, istAktiv, terminePerTag, googlePerTag, onMonatKlick, onTagKlick }) {
  const anzahlTage = tageImMonat(jahr, monat);
  const erster = new Date(jahr, monat, 1);
  const startOffset = wochentagMoBase(erster);
  const heute = new Date();
  const heuteDatum = isoDatum(heute.getFullYear(), heute.getMonth(), heute.getDate());

  const zellen = [];
  const gesamtZellen = Math.ceil((startOffset + anzahlTage) / 7) * 7;
  for (let i = 0; i < gesamtZellen; i++) {
    const tagNum = i - startOffset + 1;
    if (tagNum < 1 || tagNum > anzahlTage) { zellen.push(null); continue; }
    const datum = isoDatum(jahr, monat, tagNum);
    const termine = terminePerTag[datum] || [];
    const google = googlePerTag[datum] || [];
    zellen.push({
      tag: tagNum,
      istHeute: datum === heuteDatum,
      punkteVideo: termine.some(t => t.type === 'video'),
      punkteHaus: termine.some(t => t.type !== 'video'),
      privat: google.length > 0,
    });
  }

  return (
    <div
      onClick={onMonatKlick}
      className={`cursor-pointer rounded-lg border p-2 transition-all hover:shadow-md ${istAktiv ? 'border-fuchsia-700 shadow-md bg-fuchsia-50/40' : 'border-gray-200 bg-white'}`}
      style={istAktiv ? { borderColor: CI.brombeer } : {}}
    >
      <div className={`text-xs font-bold mb-2 text-center ${istAktiv ? '' : 'text-gray-700'}`} style={istAktiv ? { color: CI.brombeer } : {}}>
        {MONATE_KURZ[monat]}
      </div>
      <div className="grid grid-cols-7 gap-0.5 mb-0.5">
        {WOCHENTAGE_MINI.map((w, i) => (
          <div key={i} className="text-[9px] text-gray-400 text-center">{w}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-0.5">
        {zellen.map((z, i) => {
          if (!z) return <div key={i} className="aspect-square"></div>;
          return (
            <div
              key={i}
              onClick={(e) => {
                if (!onTagKlick) return;
                e.stopPropagation();   // sonst feuert auch der Monats-Klick
                onTagKlick(z.tag);
              }}
              className="aspect-square flex flex-col items-center justify-center relative rounded hover:bg-fuchsia-100/50"
              title={`${z.tag}. ${MONATE_KURZ[monat]} — Klick öffnet Wochenansicht`}
            >
              <span
                className={`text-[10px] ${z.istHeute ? 'font-bold text-white rounded-full w-4 h-4 flex items-center justify-center' : 'text-gray-600'}`}
                style={z.istHeute ? { backgroundColor: CI.brombeer } : {}}
              >
                {z.tag}
              </span>
              <div className="flex gap-0.5 mt-0.5 h-1">
                {z.punkteHaus && <span className="w-1 h-1 rounded-full" style={{ backgroundColor: CI.green }}></span>}
                {z.punkteVideo && <span className="w-1 h-1 rounded-full" style={{ backgroundColor: CI.blue }}></span>}
                {z.privat && <span className="w-1 h-1 rounded-full bg-slate-400"></span>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ==========================================================================
// Wochenansicht als modales Overlay (Stunden-Raster 8:00 - 20:00)
// ==========================================================================
function WochenAnsicht({ jahr, kw, terminePerTag, googlePerTag, kunde, onClose, onBackToAkte, onLeerKlick, onTerminKlick, onWeekChange }) {
  const montag = montagDerKW(jahr, kw);

  // Navigation: Montag um +/-7 Tage verschieben und daraus jahr+kw ableiten.
  const wechseleWoche = (delta) => {
    const m = new Date(montag);
    m.setDate(m.getDate() + delta * 7);
    const neueKW = isoKW(m);
    let neuesJahr = m.getFullYear();
    // Jahreswechsel: KW 52/53 im Januar gehoert zum Vorjahr, KW 1 im
    // Dezember zum Folgejahr — sonst zeigen wir auf das falsche Jahr.
    if (m.getMonth() === 0 && neueKW >= 52) neuesJahr = m.getFullYear() - 1;
    else if (m.getMonth() === 11 && neueKW === 1) neuesJahr = m.getFullYear() + 1;
    onWeekChange?.(neuesJahr, neueKW);
  };
  const heute = new Date();
  const heuteDatum = isoDatum(heute.getFullYear(), heute.getMonth(), heute.getDate());

  // Fuer jeden der 7 Tage: Metadaten + Termine + Google-Events.
  const tage = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(montag);
    d.setDate(d.getDate() + i);
    const di = isoDatum(d.getFullYear(), d.getMonth(), d.getDate());
    tage.push({
      date: d,
      datum: di,
      label: WOCHENTAGE[i],
      tagMonat: `${d.getDate()}. ${MONATE_KURZ[d.getMonth()]}`,
      istHeute: di === heuteDatum,
      istWochenende: i >= 5,
      termine: terminePerTag[di] || [],
      google: googlePerTag[di] || [],
    });
  }

  // Stunden von 8 bis 19 (20 ist die obere Grenze, 19:00 - 20:00 ist die letzte Zeile).
  const stunden = [];
  for (let h = 8; h < 20; h++) stunden.push(h);

  // Filter pro Tag & Stunde
  const terminInStunde = (tag, h) =>
    tag.termine.filter(t => {
      const tt = (t.time || '').slice(0, 2);
      return tt && parseInt(tt, 10) === h;
    });
  const googleInStunde = (tag, h) =>
    tag.google.filter(g => {
      if (g.all_day) return h === 8;   // ganztaegige Events einmal oben anzeigen
      const parsed = parseIsoLokal(g.start);
      return parsed.uhrzeit && parseInt(parsed.uhrzeit.slice(0, 2), 10) === h;
    });

  const sonntag = new Date(montag);
  sonntag.setDate(montag.getDate() + 6);
  const bereichLabel = `${montag.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })} – ${sonntag.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}`;

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[65] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-7xl max-h-[95vh] overflow-hidden flex flex-col">
        <div className="p-5 border-b border-gray-100 bg-slate-50 flex justify-between items-center gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            {kunde && onBackToAkte && (
              <button
                onClick={onBackToAkte}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-bold hover:bg-fuchsia-50 transition-colors border"
                style={{ color: CI.brombeer, borderColor: CI.brombeer }}
                title={`Direkt zurück zur Akte von ${kunde.firstName} ${kunde.lastName}`}
              >
                <ArrowLeft className="w-4 h-4" />
                Zurück zur Akte von {kunde.firstName} {kunde.lastName}
              </button>
            )}
            <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
              <CalendarIcon className="w-5 h-5" style={{ color: CI.brombeer }} />
              KW {kw} / {jahr}
              <span className="text-sm font-normal text-gray-500 ml-2">{bereichLabel}</span>
            </h2>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => wechseleWoche(-1)}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-bold text-gray-700 hover:bg-gray-200 transition-colors"
              title="Vorherige Woche"
            >
              <ChevronLeft className="w-4 h-4" /> Vorwoche
            </button>
            <button
              onClick={() => wechseleWoche(1)}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-bold text-gray-700 hover:bg-gray-200 transition-colors"
              title="Nächste Woche"
            >
              Folgewoche <ChevronRight className="w-4 h-4" />
            </button>
            <button onClick={onClose} className="ml-2 text-gray-400 hover:text-gray-600 hover:bg-gray-200 p-2 rounded-lg" title="Schließen (zurück zum Monat)">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="overflow-auto flex-1">
          <div className="grid min-w-[900px]" style={{ gridTemplateColumns: '4.5rem repeat(7, 1fr)' }}>
            {/* Kopfzeile: Wochentage */}
            <div className="sticky top-0 z-10 bg-white border-b border-r border-gray-200 p-2 text-[11px] font-bold text-gray-400 text-center">
              Uhrzeit
            </div>
            {tage.map(t => (
              <div
                key={t.datum}
                className={`sticky top-0 z-10 bg-white border-b border-gray-200 p-2 text-center ${t.istWochenende ? 'bg-slate-50' : ''}`}
                style={t.istHeute ? { borderBottomColor: CI.brombeer, borderBottomWidth: 2 } : {}}
              >
                <div className="text-xs font-bold text-gray-500">{t.label}</div>
                <div className={`text-sm font-bold ${t.istHeute ? '' : 'text-gray-800'}`} style={t.istHeute ? { color: CI.brombeer } : {}}>
                  {t.tagMonat}
                </div>
              </div>
            ))}

            {/* Stunden-Zeilen */}
            {stunden.map(h => (
              <React.Fragment key={h}>
                <div className="border-b border-r border-gray-100 bg-slate-50/60 p-2 text-[11px] text-gray-400 text-right align-top">
                  {String(h).padStart(2, '0')}:00
                </div>
                {tage.map(t => {
                  const termineHier = terminInStunde(t, h);
                  const googleHier = googleInStunde(t, h);
                  const hatWas = termineHier.length + googleHier.length > 0;
                  return (
                    <div
                      key={t.datum + ':' + h}
                      onClick={() => onLeerKlick(t.datum, `${String(h).padStart(2, '0')}:00`)}
                      className={`border-b border-gray-100 min-h-[56px] p-1 cursor-pointer hover:bg-fuchsia-50/40 transition-colors ${t.istWochenende ? 'bg-slate-50/70' : 'bg-white'}`}
                    >
                      {termineHier.map(t2 => (
                        <button
                          key={t2.id}
                          onClick={(e) => { e.stopPropagation(); onTerminKlick(t2); }}
                          className="block w-full text-left px-1.5 py-0.5 mb-0.5 rounded text-[11px] font-bold text-white hover:opacity-80 truncate"
                          style={{ backgroundColor: t2.type === 'video' ? CI.blue : CI.green }}
                          title={`${t2.time} ${t2.lastName}, ${t2.firstName}`}
                        >
                          <span className="inline-flex items-center gap-1">
                            {t2.type === 'video' ? <Video className="w-2.5 h-2.5" /> : <MapPin className="w-2.5 h-2.5" />}
                            {t2.time} {t2.lastName || '—'}
                          </span>
                        </button>
                      ))}
                      {googleHier.map(g => {
                        const { uhrzeit } = parseIsoLokal(g.start);
                        return (
                          <div
                            key={g.google_event_id}
                            onClick={(e) => e.stopPropagation()}
                            className="block px-1.5 py-0.5 mb-0.5 rounded text-[11px] text-slate-700 bg-slate-200 border border-slate-300 truncate cursor-not-allowed"
                            title={`Privat: ${g.summary}`}
                          >
                            <span className="inline-flex items-center gap-1">
                              <Lock className="w-2.5 h-2.5" />
                              {g.all_day ? 'ganztägig' : uhrzeit} {g.summary.length > 12 ? 'Privat' : g.summary}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </React.Fragment>
            ))}
          </div>
        </div>

        <div className="p-3 border-t border-gray-100 bg-slate-50 text-xs text-gray-500 text-center">
          Klick auf leeren Slot legt einen Termin zu der Stunde an. Klick auf einen Termin öffnet den Verschieben-Dialog.
        </div>
      </div>
    </div>
  );
}
